"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { TriangleAlert as AlertTriangle, Clock, Droplets, Activity, PhoneCall, ShieldCheck, Flame, House as Home, Waves } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const floodCauses = [{
  id: "supply-lines",
  icon: Droplets,
  title: "Supply line ruptures",
  desc: "Pressurized PEX, copper, and braided flex lines can release hundreds of gallons per hour behind finish walls."
}, {
  id: "sewer-backflow",
  icon: Waves,
  title: "Sewer and drain backups",
  desc: "Municipal storm surges and line blocks drive contaminated category 3 blackwater straight into ground floors."
}, {
  id: "storm-roof",
  icon: Home,
  title: "Storm and roof failures",
  desc: "Severe winds and wind-driven rain puncture flashing and shingles, channeling structural cascades into framing."
}, {
  id: "appliances",
  icon: Flame,
  title: "Failed appliances",
  desc: "Water heater tank failures, frozen drain pans, and burst washing machine hoses pool silently beneath floorboards."
}];
export default function Overview() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1,
        duration: 0.3
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 12
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="overview" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 border-b border-border overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Top Technical Spec Bar */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border pb-4 mb-10 text-xs font-mono uppercase tracking-wider text-muted-foreground">
          <div className="flex items-center gap-2">
            <span className="inline-block w-2 h-2 rounded-full bg-primary animate-pulse" />
            <span>24/7 Field Diagnostics</span>
            <span className="hidden sm:inline text-border">|</span>
            <span className="hidden sm:inline">MD - DC - Northern VA</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-primary font-semibold">IICRC S500 Standards</span>
            <span className="text-border">|</span>
            <span>Direct Insurance Billing</span>
          </div>
        </div>

        {/* Header Block */}
        <div className="max-w-3xl mb-12">
          <Badge variant="outline" className="mb-3 border-primary/30 text-primary font-mono tracking-wider">
            Critical response window
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight font-default text-foreground mb-4 leading-tight">
            Why the first 24 hours determine your recovery
          </h2>
          <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed">
            Fast extraction stops hidden moisture before drywall crumbles, subfloors warp, and hazardous microbial colonies take hold inside your home.
          </p>
        </div>

        {/* Two-Column Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start">
          {/* Left Column: Causes & Physics of Damage */}
          <div className="lg:col-span-7 space-y-8">
            <div className="prose prose-slate max-w-none text-foreground leading-relaxed space-y-4">
              <p>
                Standing water behaves unpredictably once it escapes containment. Moisture travels along structural joists, sinks through insulation, and collects in enclosed wall cavities. Without industrial vacuum extraction and psychrometric dehumidification within 24 to 48 hours, clean Category 1 water rapidly shifts to Category 2 or 3 as it dissolves gypsum, weakens adhesive bonds, and invites aggressive mold colonization.
              </p>
              <p className="text-muted-foreground">
                TriServe dispatches local crews equipped with non-invasive thermal sensors and penetrating pin meters to chart hidden water paths, extract standing pools, and stabilize relative humidity before structural framing suffers permanent deflection.
              </p>
            </div>

            {/* 4 Primary Causes Cards */}
            <div>
              <h3 className="text-xl font-bold font-default text-foreground mb-4">
                Common triggers for interior structural flooding
              </h3>
              <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
              once: true,
              amount: 0.15
            }} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {floodCauses.map((item, i) => {
                const Icon = item.icon;
                return <motion.div key={item.id} variants={itemVariants} data-index={i}>
                      <Card className="h-full border border-border bg-card hover:border-primary/50 transition-[border-color,box-shadow] duration-200 shadow-2xs hover:shadow-sm">
                        <CardContent className="p-5 flex flex-col justify-between h-full">
                          <div>
                            <div className="w-9 h-9 rounded-md bg-primary/10 flex items-center justify-center text-primary mb-3">
                              <Icon className="w-5 h-5" />
                            </div>
                            <h4 className="text-base font-bold text-foreground mb-1.5 font-default">
                              {item.title}
                            </h4>
                            <p className="text-xs sm:text-sm text-muted-foreground leading-normal">
                              {item.desc}
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>;
              })}
              </motion.div>
            </div>

            {/* Critical Alert Callout */}
            <div className="rounded-lg border border-border bg-muted/60 p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="w-10 h-10 rounded-md bg-primary text-primary-foreground flex items-center justify-center shrink-0">
                <Clock className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-bold text-foreground">
                  The 48-hour microbial proliferation threshold
                </h4>
                <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
                  Fungal spores begin active germination within 48 hours of saturation. Timely commercial extraction prevents secondary insurance remediation claim complications.
                </p>
              </div>
              <Button asChild size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90 shrink-0 font-bold">
                <a href="tel:855-435-6631">
                  <PhoneCall className="w-3.5 h-3.5 mr-1" />
                  Call 855-435-6631
                </a>
              </Button>
            </div>
          </div>

          {/* Right Column: Documentary Photography & Telemetry Card */}
          <div className="lg:col-span-5 space-y-6">
            <div className="relative rounded-lg border border-border bg-card shadow-md overflow-hidden">
              {/* Image with hairline frame */}
              <div className="relative aspect-[4/3] sm:aspect-[16/11] w-full bg-muted overflow-hidden">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/water_damage_wall_moisture_meter_inspection.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/water_damage_wall_moisture_meter_inspection.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/water_damage_wall_moisture_meter_inspection.png-wvc-sizes" alt="TriServe certified technician testing interior wall moisture depth with calibrated digital meter" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                
                {/* Inset Telemetry Spec Badge */}
                <div className="absolute top-3 left-3 bg-background/95 backdrop-blur-xs border border-border rounded-md px-3 py-1.5 shadow-xs flex items-center gap-2 font-mono text-[11px] text-foreground">
                  <span className="w-2 h-2 rounded-full bg-primary" />
                  <span>CALIBRATED PIN METER: ACTIVE</span>
                </div>

                {/* Bottom Moisture Spec Overlay */}
                <div className="absolute bottom-3 inset-x-3 bg-foreground/90 text-background backdrop-blur-xs rounded-md p-3 border border-border/20 flex items-center justify-between text-xs">
                  <div>
                    <span className="block font-mono text-[10px] uppercase text-muted-foreground/90">Cavity Saturation</span>
                    <span className="font-mono font-bold text-sm text-background">34.8% WME (Elevated)</span>
                  </div>
                  <div className="text-right">
                    <span className="block font-mono text-[10px] uppercase text-muted-foreground/90">Dry Standard</span>
                    <span className="font-mono font-bold text-sm text-accent">12.0% WME</span>
                  </div>
                </div>
              </div>

              {/* Caption & Technical Explainer */}
              <div className="p-5 border-t border-border bg-card">
                <div className="flex items-center gap-2 mb-2 text-xs font-mono uppercase tracking-wider text-primary font-bold">
                  <Activity className="w-3.5 h-3.5" />
                  <span>On-Site Diagnostics Protocol</span>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                  Visual inspection cannot identify trapped water inside insulated drywall or beneath hardwood planks. TriServe technicians take baseline moisture readings across all structural substrates before configuring commercial desiccant dryers.
                </p>

                {/* Direct Checklist */}
                <div className="space-y-2 border-t border-border pt-4">
                  <div className="flex items-center gap-2 text-xs text-foreground font-medium">
                    <ShieldCheck className="w-4 h-4 text-primary shrink-0" />
                    <span>Non-destructive thermal infrared perimeter scans</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-foreground font-medium">
                    <ShieldCheck className="w-4 h-4 text-primary shrink-0" />
                    <span>Moisture mapping logged daily for insurance adjusters</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-foreground font-medium">
                    <ShieldCheck className="w-4 h-4 text-primary shrink-0" />
                    <span>Direct dispatch: no call centers, certified local technicians</span>
                  </div>
                </div>

                <div className="mt-5 pt-4 border-t border-border flex flex-col sm:flex-row gap-2.5">
                  <Button asChild className="w-full sm:w-auto flex-1 bg-accent text-accent-foreground hover:bg-accent/90 font-bold text-sm">
                    <Link to="/ContactRequestServicePage">
                      Request Inspection Now
                    </Link>
                  </Button>
                  <Button asChild variant="outline" className="w-full sm:w-auto font-medium text-sm border-border">
                    <Link to="/CommercialHOAPropertyRestorationPage">
                      Commercial Protocols
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}